import { Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React from "react";

import { patientPastMedicalHistoryApi } from "../../../apis";
import { MedicalHistoryResponseDto } from "../../../libs/cellera-sdk";
import {
  MEDICAL_HISTORY_CONDITIONS,
  MEDICAL_HISTORY_SHORT_FIELDS,
  MEDICAL_HISTORY_LONG_FIELDS,
} from "../../../types/medical-history";
import IntakeCheckboxField from "./IntakeCheckboxField";
import IntakeField from "./IntakeField";
import IntakeHeader from "./IntakeHeader";
import LayoutWrapper from "./LayoutWrapper";

interface PastMedicalHistoryProps {
  patientId: string;
}

const PastMedicalHistory: React.FC<PastMedicalHistoryProps> = ({
  patientId,
}) => {
  const { data, isError, error } = useQuery({
    queryKey: ["medicalHistory", patientId],
    queryFn: async () => {
      if (!patientId) return null;
      return patientPastMedicalHistoryApi.getMedicalHistoryByPatientId({
        patientId: patientId,
      });
    },
    enabled: !!patientId,
  });

  React.useEffect(() => {
    if (isError && error) {
      console.error("MedicalHistory query error:", error);
    }
  }, [isError, error]);

  const displayData: MedicalHistoryResponseDto = data ?? {
    patientId,
    pastMedicalHistory: {
      diabetes: false,
      hypertension: false,
      heartDisease: false,
      highCholesterol: false,
      asthma: false,
      thyroidDisorder: false,
      autoimmuneDisease: false,
      ibsIbd: false,
      gerd: false,
      migraines: false,
      anxietyDepression: false,
      chronicFatigue: false,
      cancer: false,
      cancerType: "",
      neurologicalConditions: false,
      other: "",
      additionalNotes: "",
    },
    surgeriesHospitalizations: "",
    significantInjuries: "",
    allergies: "",
  };

  const renderCheckboxField = (
    condition: (typeof MEDICAL_HISTORY_CONDITIONS)[number]
  ) => {
    const value = displayData.pastMedicalHistory?.[
      condition.key as keyof typeof displayData.pastMedicalHistory
    ] as boolean;

    return (
      <IntakeCheckboxField
        key={condition.key}
        label={condition.label}
        value={value ?? false}
        disabled={true}
      />
    );
  };

  const renderShortField = (
    field: (typeof MEDICAL_HISTORY_SHORT_FIELDS)[number]
  ) => {
    let value = "";

    if (field.key === "cancerType" || field.key === "other") {
      value =
        (displayData.pastMedicalHistory?.[
          field.key as keyof typeof displayData.pastMedicalHistory
        ] as string) ?? "";
    } else {
      value =
        (displayData[field.key as keyof MedicalHistoryResponseDto] as string) ??
        "";
    }

    return (
      <IntakeField
        key={field.key}
        label={field.label}
        value={value}
        disabled={true}
      />
    );
  };

  const renderLongField = (
    field: (typeof MEDICAL_HISTORY_LONG_FIELDS)[number]
  ) => {
    const value =
      (displayData.pastMedicalHistory?.[
        field.key as keyof typeof displayData.pastMedicalHistory
      ] as string) ?? "";

    return (
      <IntakeField
        key={field.key}
        label={field.label}
        value={value}
        disabled={true}
      />
    );
  };

  return (
    <Box
      sx={{
        "&::-webkit-scrollbar": {
          display: "none",
        },
        "-ms-overflow-style": "none",
        "scrollbar-width": "none",
        "& *": {
          "&::-webkit-scrollbar": {
            display: "none",
          },
          "-ms-overflow-style": "none",
          "scrollbar-width": "none",
        },
      }}
    >
      <IntakeHeader title="Past Medical History" />

      {/* Medical Conditions - Checkboxes */}
      <LayoutWrapper itemsPerRow={2} spacing={3}>
        {MEDICAL_HISTORY_CONDITIONS.map(renderCheckboxField)}
      </LayoutWrapper>

      {/* Short Text Fields */}
      <LayoutWrapper itemsPerRow={1} spacing={3}>
        {MEDICAL_HISTORY_SHORT_FIELDS.map(renderShortField)}
      </LayoutWrapper>

      {/* Long Text Fields (Additional Notes at the end) */}
      <LayoutWrapper itemsPerRow={1} spacing={3}>
        {MEDICAL_HISTORY_LONG_FIELDS.map(renderLongField)}
      </LayoutWrapper>
    </Box>
  );
};

export default PastMedicalHistory;
